"use client";

import { useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { CALENDAR_FRAME_BOTTOM_GAP } from "./calendarFrame";

/** Decoration only: the document/workspace remains the native scroll owner. */
export default function CalendarViewportFrame({
  headerRef,
  children,
}: {
  headerRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  const topEdgeRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const edge = topEdgeRef.current;
    const grid = edge?.parentElement;
    const header = headerRef.current;
    if (!edge || !grid || !header) return;

    // Only layout changes need measurement. Scrolling is entirely CSS:
    // moving an absolute border/clip-path in rAF lags compositor scrolling.
    const update = () => {
      const stickyTop = parseFloat(getComputedStyle(header).top) || 0;
      edge.style.top = `${stickyTop + header.offsetHeight + grid.clientTop}px`;
    };

    const observer = new ResizeObserver(update);
    observer.observe(header);
    window.addEventListener("resize", update);
    update();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [headerRef]);

  return (
    <>
      <div ref={topEdgeRef} aria-hidden="true" data-calendar-frame="top"
        className="pointer-events-none sticky z-10 h-0 shrink-0">
        {/* The outer shadow masks just the rounded corners. Unlike a moving
            clip-path, these caps stay attached to the native sticky edges. */}
        <div className="absolute -inset-x-px -top-px h-[10px] rounded-t-[10px] border-t border-x border-slate-150 shadow-[0_-10px_0_10px_white]" />
      </div>
      {children}
      <div aria-hidden="true" data-calendar-frame="bottom"
        className="pointer-events-none sticky z-10 h-0 shrink-0"
        style={{ bottom: CALENDAR_FRAME_BOTTOM_GAP + 1 }}>
        <div className="absolute -inset-x-px -bottom-px h-[10px] rounded-b-[10px] border-b border-x border-slate-150 shadow-[0_10px_0_10px_white]" />
      </div>
    </>
  );
}
